import React from "react";
import { CubeTransparentIcon, LinkIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import type { ArchitectureVisualizerProps } from "../technicalSolutionPhase.types";
import type { ArchitectureComponent } from "@/features/Projects";

const componentTypeColors = {
  frontend: "bg-blue-100 text-blue-800 border-blue-200",
  backend: "bg-green-100 text-green-800 border-green-200",
  database: "bg-purple-100 text-purple-800 border-purple-200",
  external: "bg-orange-100 text-orange-800 border-orange-200",
};

export default function ArchitectureVisualizer({
  architecture,
  onChange,
}: ArchitectureVisualizerProps) {
  const handlePatternChange = (pattern: string) => {
    onChange({
      ...architecture,
      pattern,
    });
  };

  const handleDescriptionChange = (description: string) => {
    onChange({
      ...architecture,
      description,
    });
  };

  const handleAddComponent = () => {
    const newComponent: ArchitectureComponent = {
      id: `comp-${Date.now()}`,
      name: "Novo Componente",
      type: "frontend",
      description: "",
      technologies: [],
      connections: [],
    };

    onChange({
      ...architecture,
      components: [...architecture.components, newComponent],
    });
  };

  const handleUpdateComponent = (
    index: number,
    field: keyof ArchitectureComponent,
    value: any
  ) => {
    const newComponents = [...architecture.components];
    newComponents[index] = { ...newComponents[index], [field]: value };

    onChange({
      ...architecture,
      components: newComponents,
    });
  };

  const handleRemoveComponent = (index: number) => {
    const componentId = architecture.components[index].id;
    
    // Remove the component and any connections to it
    const newComponents = architecture.components.filter((_, i) => i !== index);
    const newIntegrations = architecture.integrations.filter(
      (integration) => 
        integration.source !== componentId && integration.target !== componentId
    );

    onChange({
      ...architecture,
      components: newComponents,
      integrations: newIntegrations,
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <CubeTransparentIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Arquitetura do Sistema</h3>
      </div>

      <div className="space-y-6">
        {/* Pattern Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Padrão Arquitetural
          </label>
          <select
            value={architecture?.pattern}
            onChange={(e) => handlePatternChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Arquitetura em Camadas">Arquitetura em Camadas</option>
            <option value="Microserviços">Microserviços</option>
            <option value="Monolito">Monolito</option>
            <option value="Serverless">Serverless</option>
            <option value="Hexagonal">Hexagonal</option>
            <option value="Event-Driven">Event-Driven</option>
          </select>
        </div>

        {/* Architecture Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrição da Arquitetura
          </label>
          <textarea
            value={architecture?.description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Descreva como a arquitetura será estruturada..."
          />
        </div>

        {/* Components */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Componentes do Sistema</h4>
            <button
              onClick={handleAddComponent}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              + Adicionar Componente
            </button>
          </div>

          <div className="space-y-4">
            {(architecture?.components || []).map((component, index) => (
              <div key={component.id} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nome do Componente
                    </label>
                    <input
                      type="text"
                      value={component.name}
                      onChange={(e) =>
                        handleUpdateComponent(index, "name", e.target.value)
                      }
                      className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tipo
                    </label>
                    <select
                      value={component.type}
                      onChange={(e) =>
                        handleUpdateComponent(index, "type", e.target.value as any)
                      }
                      className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="frontend">Frontend</option>
                      <option value="backend">Backend</option>
                      <option value="database">Database</option>
                      <option value="external">Externo</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={component.description}
                    onChange={(e) =>
                      handleUpdateComponent(index, "description", e.target.value)
                    }
                    rows={2}
                    className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Descreva a função deste componente..."
                  />
                </div>

                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center space-x-2">
                    <span className={clsx(
                      "px-2 py-1 text-xs font-medium rounded border",
                      componentTypeColors[component.type]
                    )}>
                      {component.type}
                    </span>
                    {(component.technologies || []).length > 0 && (
                      <div className="flex space-x-1">
                        {(component.technologies || []).slice(0, 2).map((tech, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {tech}
                          </span>
                        ))}
                        {(component.technologies || []).length > 2 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            +{(component.technologies || []).length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveComponent(index)}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}

            {(architecture?.components || []).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CubeTransparentIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Nenhum componente definido ainda</p>
              </div>
            )}
          </div>
        </div>

        {/* Visual Representation */}
        {(architecture?.components || []).length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Visualização da Arquitetura</h4>
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-200">
              <div className="flex flex-wrap gap-4 justify-center">
                {(architecture.components || []).map((component) => (
                  <div
                    key={component.id}
                    className={clsx(
                      "px-4 py-3 rounded-lg border-2 text-center min-w-32",
                      componentTypeColors[component.type]
                    )}
                  >
                    <div className="font-medium text-sm">{component.name}</div>
                    <div className="text-xs opacity-75 capitalize">{component.type}</div>
                  </div>
                ))}
              </div>
              {(architecture.integrations || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <LinkIcon className="h-4 w-4" />
                    <span>{(architecture.integrations || []).length} integrações configuradas</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}